function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import { AttrsSchema, Component, jsx } from 'panel';
import type { VNode } from 'snabbdom';
import type { Point } from 'highcharts';
import { defineAndMakeJsxFactory } from 'common/util/jsx';
import { panel2react } from 'common/util/react';
import { ChartsContextRegistry } from 'common/charts/contexts/registry';
import { FlatSegment, InsightsQueryResults } from 'common/report/insights/util/types';
import { ChartType, InsightsBookmarkParams } from 'common/types/reports/bookmark';
import { SegmentFilterController } from 'common/charts/contexts/segment-filter-controller';
import { PieChartEl } from 'common/charts/visualization/pie-chart';
import { ChartDataItem } from 'common/charts/visualization/pie-chart/types';
import { SpinnerEl } from 'common/components/spinner';
import { SegmentLegendHeaderEl } from 'common/charts/misc/segment-legend-header/panel';
import { EmptyDataEl } from 'common/charts/misc/empty-data';
import type { PopoverCallback } from 'common/charts/types';
import { ContextMenuEl } from 'common/widgets/context-menu';
import { OTHER_SEGMENT_VALUE } from 'common/report/insights/util/constants';
import { colors } from 'common/util/colors';
import { computeMetricClauseByKey, renderPieChartTooltip } from 'common/report/insights/util/tooltip';
import { DataGroup } from 'common/widgets/data-group-selector/types';
import { dataGroupStore } from 'common/report/stores/data-groups';
import { getInsightsAggregateCsvExportLimit, getWorkspaceId, isPublicMode } from 'common/report/util';
import { REPORT_LIMITS, ReportName } from 'common/report/constants';
import { canShowOtherSegment, getSingleNumberFromSegmentValue, getSortConfig } from 'common/report/insights/util';
import { ParamsAndResults } from 'common/report/types';
import { CurrentProject, CurrentProjectResource } from 'common/report/model-resources/projects';
import { deselectPoint, selectPoint } from 'common/report/util/highcharts';
import { renamePropertyValue } from 'common/report/util/properties';
import { isCustomCsvLimitEnabled } from 'common/widgets/csv-export-modal/utils';
import { formatMetricNumberWithUnits } from 'common/util';
import { eventPropertyDefinitionsStore } from 'common/report/stores/property-definitions';
import { ClauseType } from 'iron/common/report/insights/models/types';
import { getFinalDisplayOptions } from '../util';
import css from './index.inline.styl';
import { getOtherSegment, getPieChartData, PieChartData } from './util';
interface InsightsPieChartState {
  pieChartData: PieChartData | null;
  /** clicked highcharts point object to display a popover for */
  popoverSelection: Point | null;
  /** holds positioning of the popover for the active highcharts point */
  popoverStyles: {
    visibility?: string;
    left?: string;
    top?: string;
  } | null;
  popoverSegment: FlatSegment | null;
}
export interface InsightsPieChartJsxProps {
  attrs: {
    loaded: boolean;
    failed?: boolean;
  };
  props: {
    // use a single prop for bookmark params and results so they can be updated together atomically
    paramsAndResults: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults> | null;

    // dataGroups will automatically be obtained from Stores without this prop,
    // but this prop is available for environments where Stores aren't supported (eg. Public Dashboards)
    dataGroups?: Record<string, DataGroup>;
    popoverCalback?: PopoverCallback;
  };
}
export class InsightsPieChart extends Component<InsightsPieChartState, InsightsPieChartJsxProps[`attrs`], any, any, ChartsContextRegistry> {
  constructor() {
    super(...arguments);
    _defineProperty(this, "_bookmarkParams", null);
    _defineProperty(this, "_results", null);
    _defineProperty(this, "_dataGroups", null);
    _defineProperty(this, "popoverCallback", null);
    _defineProperty(this, "resourceCustomSubscriptions", new Map([[CurrentProjectResource, () => {
      if (!this.dataGroups) {
        const currentProject = this.currentProject;
        if (currentProject) {
          const projectId = currentProject.id;
          const workspaceId = parseInt(getWorkspaceId(), 10);
          // Note: data groups are only used in tooltips and don't need to trigger state updates/renders or be annotated with people properties
          dataGroupStore.fetch({
            projectId,
            workspaceId
          }); // async, no need to wait
        }
      }
    }]]));
    _defineProperty(this, "handlePointClick", point => {
      var _this$popoverCallback;
      const {
        series,
        options
      } = point;
      const segmentKey = options.name;
      const {
        segmentLookup
      } = this.state.pieChartData;
      const popoverSegment: FlatSegment = segmentLookup.get(segmentKey);
      const left = series.chart.tooltip.now.x;
      const top = series.chart.tooltip.now.y;
      const segment = popoverSegment.path[popoverSegment.path.length - 1];
      const segments = popoverSegment.path;
      const hasPopoverActions = (_this$popoverCallback = this.popoverCallback) === null || _this$popoverCallback === void 0 ? void 0 : _this$popoverCallback.call(null, segment, segments).length;
      if (hasPopoverActions) {
        selectPoint(point);
        this.update({
          popoverStyles: {
            left: "".concat(left, "px"),
            top: "".concat(top, "px")
          },
          popoverSelection: point,
          popoverSegment
        });
      }
    });
  }
  static get attrsSchema(): AttrsSchema<InsightsPieChartJsxProps[`attrs`]> {
    return _objectSpread(_objectSpread({}, super.attrsSchema), {}, {
      loaded: "boolean",
      failed: "boolean"
    });
  }
  public get bookmarkParams(): InsightsBookmarkParams | null {
    return this._bookmarkParams;
  }
  public get results(): InsightsQueryResults | null {
    return this._results;
  }
  public set paramsAndResults(_ref: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults> | null) {
    let {
      params,
      results
    }: ParamsAndResults<InsightsBookmarkParams, InsightsQueryResults> | null = _ref;
    if (!isEqual(this.bookmarkParams, params) || !isEqual(this.results, results)) {
      this._bookmarkParams = params;
      this._results = results;
      this.updatePieChartData();
      this.updatePersistedLegendSelections();
    }
  }
  public get dataGroups(): Record<string, DataGroup> | null {
    return this._dataGroups;
  }
  public set dataGroups(value: Record<string, DataGroup> | null) {
    this._dataGroups = value;
  }
  private get segmentFilterController(): SegmentFilterController {
    return this.getContext("segmentFilterController");
  }
  private get currentProject(): CurrentProject | undefined {
    return isPublicMode() ? undefined : this.getContext("currentProject").get().value;
  }
  get config() {
    return {
      useShadowDom: true,
      css,
      template: () => this.renderTemplate(),
      defaultState: {
        pieChartData: null,
        popoverSelection: null,
        popoverStyles: null,
        popoverSegment: null
      },
      contexts: ["segmentFilterController" as const, "currentProject" as const, "i18n" as const, "metrics" as const]
    };
  }
  connectedCallback() {
    super.connectedCallback();
    this.updatePieChartData();
    this.updatePersistedLegendSelections();
  }
  private formatValue(metricKey: string, value: string | number): string {
    let forTooltip = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    const metricClauseByKey = computeMetricClauseByKey({
      sectionsMetadata: this.results.meta.report_sections,
      bookmarkParams: this.bookmarkParams,
      dataGroups: Object.values(this.getDataGroups())
    });
    const clause = metricClauseByKey[metricKey];
    return formatMetricNumberWithUnits(value, getFinalDisplayOptions((clause === null || clause === void 0 ? void 0 : clause.clauseType) === ClauseType.Show ? clause : null, eventPropertyDefinitionsStore, this.getContext("metrics"), {},
    // Tooltip values should never be abbreviated.
    forTooltip ? {
      abbrev: false
    } : {}));
  }
  private renderTemplate(): VNode {
    var _this$results;
    const loaded = this.attr("loaded") && this.hasRequiredParams() && !!this.state.pieChartData;
    if (!loaded) {
      return <div sel='.root'>
          <SpinnerEl attrs={{
          elref: "loadingSpinner"
        }} />
        </div>;
    }
    if (isEmpty((_this$results = this.results) === null || _this$results === void 0 ? void 0 : _this$results.series)) {
      return <div sel='.root'>
          <EmptyDataEl />
        </div>;
    }
    const visiblePieSeries: ChartDataItem[] = this.segmentFilterController.getSelectedItems(0).map(item => {
      const segment = this.state.pieChartData.segmentLookup.get(item.id);
      return {
        color: this.segmentFilterController.getItemColor(0, item.id),
        name: item.id,
        displayName: item.label,
        value: getSingleNumberFromSegmentValue(segment.value)
      };
    });

    // TODO pjboudrx maybe don't do this re-calc on every render?
    const otherSegment = canShowOtherSegment(this.bookmarkParams) ? getOtherSegment(this.state.pieChartData.segmentLookup, this.segmentFilterController, this.state.pieChartData.overallTotal) : null;
    if ((otherSegment === null || otherSegment === void 0 ? void 0 : otherSegment.totalValue) > 0) {
      visiblePieSeries.push({
        color: colors.otherSegment.primary,
        name: OTHER_SEGMENT_VALUE,
        displayName: "Other",
        secondaryColor: null,
        value: otherSegment.totalValue
      });
    }
    const metricClauseByKey = computeMetricClauseByKey({
      sectionsMetadata: this.results.meta.report_sections,
      bookmarkParams: this.bookmarkParams,
      dataGroups: Object.values(this.getDataGroups())
    });
    const tooltipFormatter = _ref2 => {
      let {
        name,
        value,
        percent,
        color
      } = _ref2;
      if (name === OTHER_SEGMENT_VALUE) {
        return renderPieChartTooltip({
          color,
          formatter: v => this.formatValue(name, v, true),
          metricClauseByKey,
          i18n: this.getContext("i18n"),
          otherMetricKeys: otherSegment.metricKeys,
          percent,
          value
        });
      }
      const segment = this.state.pieChartData.segmentLookup.get(name);
      const metricKey = segment.path[0];
      return renderPieChartTooltip({
        color,
        formatter: v => this.formatValue(metricKey, v, true),
        metricKey,
        metricClauseByKey,
        i18n: this.getContext("i18n"),
        percent,
        segments: segment.path.slice(1).map((v, index) => renamePropertyValue(v, this.results.headers[index + 1], this.bookmarkParams.sections.group[index])),
        value
      });
    };
    const visibleSegmentIds = visiblePieSeries.map(segment => {
      return segment.name;
    });
    return <div sel='.root'>
        <SegmentLegendHeaderEl visibleSegmentIds={visibleSegmentIds} />
        <PieChartEl sel='.pie-chart' props={{
        chartData: visiblePieSeries,
        tooltipFormatter: metricClauseByKey ? tooltipFormatter : null,
        onPieChartClick: this.popoverCallback && this.handlePointClick
      }} params={{
        getMetricKeyForName: name => {
          const segment = this.state.pieChartData.segmentLookup.get(name);
          return segment ? segment.path[0] : name;
        },
        valueFormatter: (metricKey, value) => this.formatValue(metricKey, value)
      }} attrs={{
        elref: "pieChart"
      }} />
        {/* render point popover */}
        {this.renderPopover()}
      </div>;
  }
  renderPopover() {
    const {
      popoverSelection,
      popoverSegment,
      popoverStyles
    } = this.state;
    if (!popoverSelection || !this.popoverCallback) {
      return <></>;
    }
    const segment = popoverSegment.path[popoverSegment.path.length - 1];
    const segments = popoverSegment.path;
    return <ContextMenuEl params={{
      menuItems: this.popoverCallback(segment, segments),
      relativePositioning: true,
      open: true,
      position: {
        left: 0,
        top: 0
      }
    }} style={popoverStyles} on={{
      action: (ev: CustomEvent) => this.handlePopoverAction(ev),
      close: () => this.handlePopoverClose()
    }} />;
  }
  handlePopoverAction(ev: CustomEvent) {
    const {
      path
    } = this.state.popoverSegment;
    const {
      color
    } = this.state.popoverSelection;
    this.dispatchEvent(new CustomEvent("popoverAction", {
      detail: {
        color,
        segment: path,
        action: ev.detail.action
      }
    }));
    this.handlePopoverClose();
  }
  handlePopoverClose() {
    const selectedPoint = this.state.popoverSelection;
    if (selectedPoint) {
      deselectPoint(selectedPoint);
    }
    this.update({
      popoverSelection: null,
      popoverStyles: null,
      popoverSegment: null
    });
  }
  private getDataGroups(): Record<string, DataGroup> {
    if (this.dataGroups) {
      return this.dataGroups;
    } else {
      return dataGroupStore.value;
    }
  }
  private hasRequiredParams(): boolean {
    return !!(this.isConnected && this.bookmarkParams && this.results);
  }
  private updatePersistedLegendSelections(): void {
    var _this$bookmarkParams;
    const individualSelectedSegments = (_this$bookmarkParams = this.bookmarkParams) === null || _this$bookmarkParams === void 0 || (_this$bookmarkParams = _this$bookmarkParams.legend) === null || _this$bookmarkParams === void 0 ? void 0 : _this$bookmarkParams.individualSelectedSegments;
    if (this.isConnected && individualSelectedSegments) {
      this.segmentFilterController.loadFromSavedLegend(individualSelectedSegments ? [individualSelectedSegments] : null);
    }
  }
  private updatePieChartData(): void {
    if (this.hasRequiredParams()) {
      var _meta$queryLimits;
      const pieChartData = getPieChartData(this.bookmarkParams, this.getContext("metrics"), this.results, this.getDataGroups(), this.getContext("i18n"));
      this.update({
        pieChartData
      });
      const {
        meta
      } = this.results;
      const {
        apiResultsTruncated
      } = meta;
      this.segmentFilterController.loadSections([{
        items: pieChartData.legendSegmentItems,
        readOnlyItems: pieChartData.readOnlyLegendItems,
        tableColumns: pieChartData.tableColumns,
        metadata: pieChartData.legendIdToIndexMap,
        // Insights app is expected to use this mapping to persist legend selection indices to the URL
        apiResultsTruncated
      }], {
        tableSortConfig: getSortConfig({
          bookmarkSortConfig: this.bookmarkParams.sorting,
          headerCount: this.results.headers.length
        })[ChartType.Pie],
        defaultNumSelections: REPORT_LIMITS[ReportName.Insights].segments[ChartType.Pie].default,
        maxNumSelections: REPORT_LIMITS[ReportName.Insights].segments[ChartType.Pie].max
      });
      this.segmentFilterController.segmentationQueryLimit = (_meta$queryLimits = meta.queryLimits) === null || _meta$queryLimits === void 0 ? void 0 : _meta$queryLimits.limit;
      this.segmentFilterController.exportSegmentationQueryLimit = isCustomCsvLimitEnabled(this.currentProject) ? getInsightsAggregateCsvExportLimit(this.currentProject) : REPORT_LIMITS[ReportName.Insights].csvExport;
    } else {
      this.update({
        pieChartData: null
      });
    }
  }
}
export const InsightsPieChartEl = defineAndMakeJsxFactory<InsightsPieChartJsxProps>("mp-insights-pie-chart", InsightsPieChart);
export const InsightsPieChartReact = panel2react<InsightsPieChartJsxProps>("mp-insights-pie-chart", InsightsPieChart);